import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "@modalSrc/Modal";
import ModalFormFields from "@modalSrc/ModalFormFields";
import {
    deleteBillingOrgAsync,
    formatAccountingUserName,
    loadBillingOrgAccountingUsersAsync,
    saveBillingOrgDetailsAsync,
    saveBillingOrgUsersAsync,
    type AccountingUserOption,
    type BillingOrgDetails,
} from "@/utils/billingOrgHelpers";

type BillingOrgModalProps = {
    isOpen: boolean;
    org: BillingOrgDetails | null;
    onClose: () => void;
    updateSpinner?: (loading: boolean) => void;
};

export default function BillingOrgModal({ isOpen, org, onClose, updateSpinner }: BillingOrgModalProps) {
    const [formData, setFormData] = useState<BillingOrgDetails>(() => ({
        id: 0,
        name: "",
        legalName: "",
        remitToAddress: "",
        remitToEmail: "",
        taxId: "",
        directorUserId: null,
    }));
    const [userOptions, setUserOptions] = useState<AccountingUserOption[]>([]);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

    useEffect(() => {
        if (!isOpen) return;

        setFormData(org ?? {
            id: 0,
            name: "",
            legalName: "",
            remitToAddress: "",
            remitToEmail: "",
            taxId: "",
            directorUserId: null,
        });
        setSelectedUserIds(org?.assignedUserIds ?? []);
        loadBillingOrgAccountingUsersAsync().then(setUserOptions);
    }, [isOpen, org]);

    if (!isOpen) return null;

    function handleFieldChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
        const { name, value } = e.target;
        if (name === "directorUserId") {
            const parsed = value ? parseInt(value, 10) : 0;
            setFormData((prev) => (prev ? { ...prev, directorUserId: parsed > 0 ? parsed : null } : prev));
            return;
        }
        setFormData((prev) => (prev ? { ...prev, [name]: value } : prev));
    }

    function toggleUser(userId: number) {
        setSelectedUserIds((prev) =>
            prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
        );
    }

    async function handleSave() {
        if (!formData.name?.trim()) {
            toast.error("Name is required.");
            return;
        }

        updateSpinner?.(true);
        try {
            const resp = await saveBillingOrgDetailsAsync(formData);
            if (!resp?.ok) {
                toast.error("Unable to save billing org.");
                return;
            }

            const saved = resp.jsonData as BillingOrgDetails;
            const orgId = saved.id ?? formData.id;
            if (orgId > 0) {
                const usersResp = await saveBillingOrgUsersAsync(orgId, selectedUserIds);
                if (!usersResp?.ok) {
                    toast.error("Billing org saved, but delegate users could not be updated.");
                }
            }

            toast.success("Billing org saved.");
            onClose();
        } finally {
            updateSpinner?.(false);
        }
    }

    async function handleDelete() {
        if (!formData.id) return;
        if (!window.confirm(`Delete billing org "${formData.name}"?`)) return;

        updateSpinner?.(true);
        try {
            const resp = await deleteBillingOrgAsync(formData.id);
            if (resp?.ok) {
                toast.success("Billing org deleted.");
                onClose();
            } else {
                toast.error("Unable to delete billing org. It may still be assigned to companies.");
            }
        } finally {
            updateSpinner?.(false);
        }
    }

    const footer = (
        <>
            <div className="footerLeft">
                {formData.id > 0 && (
                    <button type="button" className="btn btn-danger iconButton" onClick={handleDelete} title="Delete billing org">
                        <MdDelete />
                    </button>
                )}
            </div>
            <div className="footerRight">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
            </div>
        </>
    );

    const fields: Record<string, ReactNode> = {
        Name: (
            <input
                id="billing-org-name"
                name="name"
                value={formData.name}
                onChange={handleFieldChange}
                required
            />
        ),
        "Legal name": (
            <input
                id="billing-org-legal-name"
                name="legalName"
                value={formData.legalName ?? ""}
                onChange={handleFieldChange}
            />
        ),
        "Remit-to address": (
            <textarea
                id="billing-org-address"
                name="remitToAddress"
                rows={4}
                value={formData.remitToAddress ?? ""}
                onChange={handleFieldChange}
            />
        ),
        "Remit-to email": (
            <input
                id="billing-org-email"
                name="remitToEmail"
                type="email"
                value={formData.remitToEmail ?? ""}
                onChange={handleFieldChange}
            />
        ),
        "Tax ID": (
            <input
                id="billing-org-tax-id"
                name="taxId"
                value={formData.taxId ?? ""}
                onChange={handleFieldChange}
            />
        ),
        "Director (RightTime user)": (
            <select
                id="billing-org-director"
                name="directorUserId"
                value={formData.directorUserId ?? ""}
                onChange={handleFieldChange}
            >
                <option value="">— None —</option>
                {userOptions.map((user) => (
                    <option key={user.id} value={user.id}>
                        {formatAccountingUserName(user)}
                    </option>
                ))}
            </select>
        ),
        "Accounting delegates": (
            <div className="wizardCheckboxTable">
                <table>
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Access</th>
                        </tr>
                    </thead>
                    <tbody>
                        {userOptions.map((user) => (
                            <tr key={user.id}>
                                <td>
                                    {user.firstName} {user.lastName}
                                    {user.email ? ` (${user.email})` : ""}
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedUserIds.includes(user.id)}
                                        onChange={() => toggleUser(user.id)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ),
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={formData.id ? "Edit Billing Org" : "Add Billing Org"} footer={footer}>
            <ModalFormFields fields={fields} />
        </Modal>
    );
}
